import {
	Component,
	EventEmitter,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppTagConfiguratorComponent } from '@datachain/ui-sdk/components/app-tag-configurator';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { ImageItem } from '@datachain/ui-sdk/components/image-picker';
import { OrderedSet } from 'immutable';
import {
	debounceTime,
	filter,
	map,
	merge,
	Observable,
	of,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { configuration } from '../../../core/configuration';
import { TagSelector } from '../../../core/store';
import { PicSelector } from '../../../project/store/pics.selector';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import { SubscriptionErrorKey } from '../../domain/diagnostic-errors.entity';
import { InternalMetadataEntity } from '../../domain/internal-metadata.entity';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	InternalMetadataForm,
	InternalMetadataFormControls,
} from './internal-metadata.form';

@Component({
	selector: 'app-internal-metadata-config',
	templateUrl: './internal-metadata-config.component.html',
	styleUrls: ['./internal-metadata-config.component.scss'],
	inputs: ['internalMetadata', 'isInViewMode'],
	providers: [InternalMetadataForm],
})
export class InternalMetadataConfigComponent extends DcBaseComponent {
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly Configuration = configuration;

	public Appearance: MatFormFieldAppearance = 'legacy';
	public InternalMetadataFormControls = InternalMetadataFormControls;
	public picSelection: ImageItem | null = null;

	@ViewChild(AppTagConfiguratorComponent)
	public tagConfiguratorComponentCmp: AppTagConfiguratorComponent | null = null;

	@Output()
	public hasInternalMetadataChanged = new EventEmitter<void>();

	public vo$: Observable<{
		internalMetadata: InternalMetadataEntity;
		isInViewMode: boolean;
		availableTags: OrderedSet<AppTagItemEntity>;
		availablePics: Array<ImageItem>;
	}>;

	public constructor(
		@Optional()
		private readonly subscriptionProvider: SubscriptionProvider,
		public readonly internalMetadataForm: InternalMetadataForm,
		private readonly tagSelector: TagSelector,
		private readonly picSelector: PicSelector
	) {
		super();
		this.cmpId = 'internal-metadata-config';

		this.toObservable<InternalMetadataEntity>('internalMetadata')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((config) => {
					this.internalMetadataForm.populate(config);
				})
			)
			.subscribe();

		this.internalMetadataForm.form.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(300),
				withLatestFrom(this.toObservable<boolean>('isInViewMode')),
				filter(([, isInViewMode]) => !isInViewMode),
				tap(() => {
					if (!this.internalMetadataForm.form.valid) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.invalidInternalMetadata:`,
							SubscriptionErrorKey.InvalidInternalMetadata
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.InvalidInternalMetadata
						);
					}
					this.hasInternalMetadataChanged.emit();
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			internalMetadata:
				this.toObservable<InternalMetadataEntity>('internalMetadata'),
			isInViewMode: merge(
				of(false),
				this.toObservable<boolean>('isInViewMode')
			),
			availableTags: this.tagSelector.getTags$(),
			availablePics: this.picSelector.getPics$().pipe(
				map((res) =>
					res.map<ImageItem>(
						(r): ImageItem => ({
							id: r.id,
							isNew: r.isNew,
							color: 'white',
							tc: 'black',
							thumbnailUrl: `/service/pictogramme_image?id=${r.id}`,
							name: r.label,
						})
					)
				)
			),
		});
	}

	public hasTagsChanged(): void {
		this.hasInternalMetadataChanged.emit();
	}

	public onPicSelection($event: ImageItem): void {
		this.picSelection = $event;
	}

	public async extractFormData(): Promise<InternalMetadataEntity> {
		let tags = new Array<AppTagItemEntity>();
		if (this.tagConfiguratorComponentCmp) {
			tags = await this.tagConfiguratorComponentCmp.getUsedTags();
		}
		const ownMetadata = this.internalMetadataForm.extract();
		return InternalMetadataEntity.build({
			id: ownMetadata?.id,
			label: ownMetadata?.label,
			description: ownMetadata?.description,
			tags: OrderedSet(tags),
			picId: this.picSelection ? +this.picSelection.id : null,
		});
	}
}
